import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { GameMode } from '@interactive-room';
import { GAME_MODE_CATALOG } from '@interactive-room';
import { MODE_VISUALS } from '../../theme/designSystem';

interface ModeCardProps {
  mode: GameMode;
  selected?: boolean;
  onSelect?: () => void;
  /** Si se pasa, la tarjeta es un enlace (Studio) */
  to?: string;
  layout?: 'grid' | 'rail';
  draftCount?: number;
}

function ModeCardRail({
  selected,
  draftCount,
  meta,
  visual,
}: {
  selected?: boolean;
  draftCount?: number;
  meta: (typeof GAME_MODE_CATALOG)[GameMode];
  visual: (typeof MODE_VISUALS)[GameMode];
}) {
  return (
    <>
      <span
        className="use-case-card__icon flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xl"
        style={{ background: visual.tint }}
        aria-hidden
      >
        {meta.icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-medium leading-tight text-[var(--sala-ink)]">
          {meta.displayName}
        </p>
        <p className="truncate text-[10px] font-medium uppercase tracking-wide text-[var(--sala-ink-dim)]">
          {visual.verb}
          {typeof draftCount === 'number' && draftCount > 0 ? ` · ${draftCount} borrador${draftCount !== 1 ? 'es' : ''}` : ''}
        </p>
      </div>
      {selected ? (
        <span
          className="sala-mode-active-dot shrink-0"
          style={{ background: visual.hue }}
          aria-label="Activo"
        />
      ) : null}
    </>
  );
}

function ModeCardGrid({
  selected,
  draftCount,
  meta,
  visual,
}: {
  selected?: boolean;
  draftCount?: number;
  meta: (typeof GAME_MODE_CATALOG)[GameMode];
  visual: (typeof MODE_VISUALS)[GameMode];
}) {
  return (
    <>
      <span
        className="use-case-card__icon"
        style={{ background: visual.tint, color: visual.deep }}
        aria-hidden
      >
        {meta.icon}
      </span>
      <span className="use-case-card__body">
        <span className="use-case-card__badge" style={{ color: visual.deep, background: visual.tint }}>
          {visual.verb}
          {typeof draftCount === 'number' && draftCount > 0
            ? ` · ${draftCount} borrador${draftCount !== 1 ? 'es' : ''}`
            : ''}
          {selected && !(draftCount && draftCount > 0) ? ' · Activo' : ''}
        </span>
        <span className="use-case-card__title">{meta.displayName}</span>
        <span className="use-case-card__desc">{meta.description}</span>
      </span>
      <span className="use-case-card__arrow" aria-hidden>
        <ArrowRight className="h-4 w-4" strokeWidth={2} />
      </span>
    </>
  );
}

/**
 * Constructores técnicos — misma morfología Micelio que las recetas (UseCaseCard).
 */
export function ModeCard({
  mode,
  selected,
  onSelect,
  to,
  layout = 'grid',
  draftCount,
}: ModeCardProps) {
  const meta = GAME_MODE_CATALOG[mode];
  const visual = MODE_VISUALS[mode];

  const className = [
    layout === 'rail' ? 'sala-mode-tile sala-mode-tile--rail group text-left' : 'use-case-card group text-left',
    selected ? (layout === 'rail' ? 'sala-mode-tile--selected' : 'use-case-card--selected') : '',
  ]
    .filter(Boolean)
    .join(' ');

  const style = {
    ['--mode-hue' as string]: visual.hue,
    ['--mode-tint' as string]: visual.tint,
    ['--mode-tint-dark' as string]: visual.tintDark,
    ['--subject-hue' as string]: visual.hue,
  } as React.CSSProperties;

  const content =
    layout === 'rail' ? (
      <ModeCardRail selected={selected} draftCount={draftCount} meta={meta} visual={visual} />
    ) : (
      <ModeCardGrid selected={selected} draftCount={draftCount} meta={meta} visual={visual} />
    );

  if (to) {
    return (
      <Link to={to} className={className} style={style}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onSelect} className={className} style={style}>
      {content}
    </button>
  );
}
